import { jsx as _jsx } from "react/jsx-runtime";
import Highcharts from "highcharts";
import merge from "@tinkoff/utils/object/mergeDeep";
import { renderToStaticMarkup } from "react-dom/server";
import { createRoot } from "react-dom/client";
import BaseChart from "../BaseChart";
import ColumnTooltip from "../../Tooltip/ColumnTooltip";
import asArray from "../../../lib/as-array";
import ColumnDistributionTooltip from "../../Tooltip/ColumnDistributionTooltip";
export default function ColumnChart(props) {
    const { type = "column", suffix = "", filter, stacking, groupPadding = 0.1, pointPadding = 0.1, xAxisType = "category", plotLine, sharedTooltip = false, yAxisLabelWidth, } = props;
    const applyColumnOptions = (options) => {
        var _a;
        let { height } = props;
        // alter label placement based on type of column chart
        let labelY = 25;
        let legendReversed = false;
        let step = 0;
        if (type === "bar") {
            labelY = 4;
            step = 2;
            legendReversed = true;
            // If bar make the height variable based on how many series there are.
            if (options.series) {
                const series = options.series[0];
                if (series && height === undefined) {
                    if (series.data.length === 1) {
                        // If only one data value then use the number of data series for height
                        height = 120 + 35;
                    }
                    else {
                        height = 85 + series.data.length * 35;
                    }
                }
            }
        }
        let point = {};
        if (stacking) {
            legendReversed = true;
        }
        //Handle rendering the stacked distribution column charts. They have a lot of properties changed to work nicely.
        if (sharedTooltip) {
            point = {
                events: {
                    mouseOver: function () {
                        const chart = this.series.chart;
                        const pointIndex = this.x;
                        //Set the other parts of the stacked bar to the hover state.
                        chart.series.forEach((series) => {
                            const matchingPoint = series.data.find((point) => {
                                return point.x === pointIndex;
                            });
                            if (matchingPoint) {
                                matchingPoint.setState("hover");
                            }
                        });
                    },
                    mouseOut: function () {
                        const chart = this.series.chart;
                        const pointIndex = this.x;
                        //Set the other parts of the stacked bar to the normal state.
                        chart.series.forEach((series) => {
                            const matchingPoint = series.data.find((point) => {
                                return point.x === pointIndex;
                            });
                            if (matchingPoint) {
                                matchingPoint.setState("normal");
                            }
                        });
                    },
                    click: function (event) {
                        const point = this;
                        const pointIndex = this.x;
                        const series = point.series;
                        const chart = series.chart;
                        const tooltipId = `speedcurve-chart-tooltip-${chart.index}`;
                        //Update point reference to the point that represents the very top of the stacked column. We do this so the tool tip is positioned above all the parts of the column.
                        //Find the matching points first.
                        const matchingPoints = [];
                        chart.series.forEach((internalSeries) => {
                            const matchingPoint = internalSeries.data.find((point) => {
                                return point.x === pointIndex;
                            });
                            if (matchingPoint) {
                                matchingPoints.push(matchingPoint);
                            }
                        });
                        const topPoint = matchingPoints[0];
                        function removeTooltip() {
                            if (chart.scTooltip) {
                                chart.scTooltip.destroy();
                            }
                        }
                        const tooltipNode = (_jsx(ColumnDistributionTooltip, { point: point }));
                        // Create a new tooltip object attached to the current chart. By
                        // doing this, we prevent Highcharts from attaching all of the default
                        // event handlers so that we have complete control over when the
                        // tooltip is shown and hidden.
                        chart.scTooltip = new Highcharts.Tooltip(chart, {
                            enabled: true,
                            outside: true,
                            useHTML: true,
                            shadow: false,
                            backgroundColor: "rgba(0, 0, 0, 0)",
                            borderWidth: 0,
                            padding: 0,
                            style: {
                                pointerEvents: "auto",
                            },
                            formatter: function () {
                                // Rendering a tooltip is a multi-step process. Step 1 is to set
                                // up the formatter to render a static tooltip so that Highcharts
                                // can figure out the dimensions of the container.
                                return `<div id="${tooltipId}">${renderToStaticMarkup(tooltipNode)}</div>`;
                            },
                        }, chart.pointer);
                        // Step 2 is to tell Highcharts to render the tooltip on the point that
                        // was clicked
                        chart.scTooltip.refresh(topPoint, event);
                        // Step 3 is to create a root in the tooltip container, and render a fully
                        // hydrated tooltip over it
                        const root = createRoot(document.getElementById(tooltipId));
                        root.render(tooltipNode);
                        const removeTooltipOnClickOutside = (event) => {
                            // Remove the tooltip if the user clicks anywhere outside of the tooltip
                            if (event.target instanceof Element && !event.target.closest(`#${tooltipId}`)) {
                                document.removeEventListener("click", removeTooltipOnClickOutside);
                                removeTooltip();
                            }
                        };
                        document.addEventListener("click", removeTooltipOnClickOutside, { capture: true });
                    },
                },
            };
        }
        let yPlotLine = [];
        if (plotLine) {
            yPlotLine = [
                {
                    color: "var(--medium-emphasis)",
                    width: 2,
                    value: plotLine,
                    zIndex: 5,
                },
            ];
        }
        const columnOptions = {
            chart: {
                type,
                height,
            },
            xAxis: {
                type: xAxisType,
                labels: {
                    y: labelY,
                },
            },
            yAxis: {
                labels: {
                    step: step,
                    formatter: function () {
                        return this.value + suffix;
                    },
                },
                plotLines: yPlotLine,
            },
            tooltip: {
                shared: sharedTooltip,
            },
            legend: {
                reversed: legendReversed,
            },
            plotOptions: {
                column: {
                    grouping: true,
                    stacking,
                },
                bar: {
                    stacking,
                    borderColor: "var(--level_4)",
                    borderWidth: 1,
                },
                series: {
                    states: {
                        inactive: {
                            enabled: false,
                        },
                    },
                    groupPadding,
                    pointPadding,
                    pointPlacement: undefined,
                    point,
                },
            },
        };
        if (yAxisLabelWidth && options.chart) {
            options.chart.marginLeft = yAxisLabelWidth;
        }
        if (props.categories) {
            columnOptions.xAxis.categories = props.categories;
        }
        else if (options.series) {
            // If there is only one data series we switch to categories which helps force stacking
            const series = options.series[0];
            if (series && series.data && series.data.length === 1) {
                if (filter) {
                    // we have a specific filter, so use that for the category
                    if (series.settings && series.filters) {
                        columnOptions.xAxis.categories = [series.filters[filter]];
                    }
                }
                else {
                    if ((_a = series.settings) === null || _a === void 0 ? void 0 : _a.label) {
                        columnOptions.xAxis.categories = asArray(series.settings.label);
                    }
                }
            }
        }
        return merge(options, columnOptions);
    };
    return (_jsx(BaseChart, Object.assign({ extending: true, tooltip: ColumnTooltip }, props, { type: type, finalChartOptions: applyColumnOptions })));
}
